/**
 * Label-object Skia drawers — ONE set of drawers, TWO consumers (the editor
 * canvas and the offscreen print rasterizer), which is what guarantees
 * preview === print (ADR 0045).
 *
 * Registered into the floor-plan engine's RendererRegistry under the label
 * object types. Content is monochrome by design (thermal is 1-bit): text and
 * codes always draw pure black regardless of theme.
 *
 * Variable context rides a const-object holder (`setLabelRenderContext`) set
 * immediately before a record/draw pass — the EDITOR sets the SAMPLE context,
 * the print path sets the real one with `forPrint: true` (which snaps module
 * sizes to whole dots so the 1-bit threshold can't alias a scannable code).
 *
 * Compiler-excluded (`services/`) — imperative drawers called at picture
 * record time.
 *
 * @module services/labelTemplates/renderers
 * @category Services
 */

import type { SkCanvas, SkFont } from '@shopify/react-native-skia';
import { PaintStyle, Skia } from '@shopify/react-native-skia';

import { getBinding, LABEL_OBJECT_TYPE } from './binding';

import type { LabelVariableContext } from '@fuze-store/print-engine/label';
import {
  EMPTY_LABEL_VARIABLE_CONTEXT,
  encodeBarcodeModules,
  encodeQrMatrix,
  interpolateTokens,
} from '@fuze-store/print-engine/label';
import type { FloorObject } from '@src/floorplan/models/object';
import { registerRenderer } from '@src/floorplan/renderer/RendererRegistry';
import type { RenderContext } from '@src/floorplan/renderer/types';

const INK = '#000000';
const QR_QUIET_MODULES = 2;
/** HRI strip height as a fraction of barcode object height. */
const HRI_FRACTION = 0.22;

const renderState: { ctx: LabelVariableContext; forPrint: boolean } = {
  ctx: EMPTY_LABEL_VARIABLE_CONTEXT,
  forPrint: false,
};

/** Set before a record/draw pass; sample ctx for design, real ctx for print. */
export const setLabelRenderContext = (
  ctx: LabelVariableContext,
  forPrint: boolean,
): void => {
  renderState.ctx = ctx;
  renderState.forPrint = forPrint;
};

const inkPaint = (): ReturnType<typeof Skia.Paint> => {
  const paint = Skia.Paint();
  paint.setColor(Skia.Color(INK));
  paint.setStyle(PaintStyle.Fill);
  paint.setAntiAlias(!renderState.forPrint); // AA gray edges fight the 1-bit threshold
  return paint;
};

const effectiveSize = (
  object: FloorObject,
): { width: number; height: number } => ({
  width: object.width * object.scale,
  height: object.height * object.scale,
});

const applyRotation = (canvas: SkCanvas, object: FloorObject): void => {
  if (object.rotation === 0) return;
  const size = effectiveSize(object);
  canvas.rotate(
    object.rotation,
    object.x + size.width / 2,
    object.y + size.height / 2,
  );
};

const ELLIPSIS = '…';

const fitText = (
  font: SkFont,
  text: string,
  maxWidth: number,
  baseSize: number,
  overflow: 'shrink' | 'truncate',
): { text: string; size: number } => {
  font.setSize(baseSize);
  if (font.getTextWidth(text) <= maxWidth) {
    return { text, size: baseSize };
  }
  if (overflow === 'shrink') {
    let size = baseSize;
    while (size > 4) {
      size -= 1;
      font.setSize(size);
      if (font.getTextWidth(text) <= maxWidth) {
        return { text, size };
      }
    }
    return { text, size: 4 };
  }
  // truncate — ellipsize at the base size.
  let out = text;
  while (out.length > 1) {
    out = out.slice(0, -1);
    if (font.getTextWidth(out + ELLIPSIS) <= maxWidth) {
      return { text: out + ELLIPSIS, size: baseSize };
    }
  }
  return { text: ELLIPSIS, size: baseSize };
};

/** Text object — content (already token-resolved for print; the editor
 * resolves against the sample context at draw time). */
const drawLabelText = (
  canvas: SkCanvas,
  object: FloorObject,
  ctx: RenderContext,
): void => {
  const font = ctx.font;
  if (font === null) return;
  // Interpolate at draw time: the editor previews SAMPLE variable values
  // (true-WYSIWYG sizing); the print path already resolved tokens upstream,
  // so this is an identity there.
  const raw = interpolateTokens(object.style?.text ?? '', renderState.ctx);
  if (raw.length === 0) return;
  const binding = getBinding(object);
  const overflow = binding?.kind === 'labelText' ? binding.overflow : 'shrink';
  const align = binding?.kind === 'labelText' ? binding.align : 'center';
  const bold = binding?.kind === 'labelText' ? binding.bold : false;
  const size = effectiveSize(object);
  const baseSize = object.style?.fontSize ?? Math.min(40, size.height);

  canvas.save();
  applyRotation(canvas, object);
  const fitted = fitText(font, raw, size.width, baseSize, overflow);
  font.setSize(fitted.size);
  const textWidth = font.getTextWidth(fitted.text);
  let x = object.x + (size.width - textWidth) / 2;
  if (align === 'left') x = object.x;
  if (align === 'right') x = object.x + size.width - textWidth;
  const y = object.y + size.height / 2 + fitted.size * 0.35;
  const paint = inkPaint();
  canvas.drawText(fitted.text, x, y, paint, font);
  if (bold) {
    // Faux-bold: second pass offset by ~2.5% of the glyph size (SemiBold base).
    canvas.drawText(
      fitted.text,
      x + Math.max(0.5, fitted.size * 0.025),
      y,
      paint,
      font,
    );
  }
  canvas.restore();
};

/** Hatched placeholder for unencodable code data (design-time affordance). */
const drawInvalidCode = (canvas: SkCanvas, object: FloorObject): void => {
  // The hatch is a DESIGN-TIME cue only — never print it. A code whose data
  // variable didn't resolve (e.g. a PRODUCT label binding the ORDER-scoped
  // `{{qr}}`) prints blank rather than a confusing hatched box the operator
  // can't fix at the register; the editor still surfaces it (forPrint=false).
  if (renderState.forPrint) return;
  const size = effectiveSize(object);
  const paint = Skia.Paint();
  paint.setColor(Skia.Color(INK));
  paint.setStyle(PaintStyle.Stroke);
  paint.setStrokeWidth(1);
  canvas.drawRect(
    Skia.XYWHRect(object.x, object.y, size.width, size.height),
    paint,
  );
  const builder = Skia.PathBuilder.Make();
  const step = 8;
  for (let off = -size.height; off < size.width; off += step) {
    builder.moveTo(object.x + Math.max(0, off), object.y + Math.max(0, -off));
    builder.lineTo(
      object.x + Math.min(size.width, off + size.height),
      object.y +
        Math.min(
          size.height,
          size.height - Math.max(0, off + size.height - size.width),
        ),
    );
  }
  canvas.drawPath(builder.detach(), paint);
};

const drawBarcodeObject = (
  canvas: SkCanvas,
  object: FloorObject,
  ctx: RenderContext,
): void => {
  const binding = getBinding(object);
  if (binding === null || binding.kind !== 'barcode') return;
  const data = interpolateTokens(binding.data, renderState.ctx);
  const modules =
    data.length > 0 ? encodeBarcodeModules(binding.symbology, data) : null;

  canvas.save();
  applyRotation(canvas, object);
  if (modules === null) {
    drawInvalidCode(canvas, object);
    canvas.restore();
    return;
  }
  const size = effectiveSize(object);
  const hriHeight =
    binding.hri === 'below' && ctx.font !== null
      ? Math.min(14, size.height * HRI_FRACTION)
      : 0;
  const barsHeight = size.height - hriHeight;
  let moduleWidth = size.width / modules.length;
  let originX = object.x;
  if (renderState.forPrint) {
    // Snap to whole dots and center the remainder — a fractional module
    // width aliases under the 1-bit threshold and kills scannability.
    moduleWidth = Math.max(1, Math.floor(moduleWidth));
    originX = object.x + (size.width - moduleWidth * modules.length) / 2;
  }
  const paint = inkPaint();
  let runStart = -1;
  for (let i = 0; i <= modules.length; i++) {
    const isBar = i < modules.length && modules[i] === true;
    if (isBar && runStart === -1) {
      runStart = i;
    } else if (!isBar && runStart !== -1) {
      canvas.drawRect(
        Skia.XYWHRect(
          originX + runStart * moduleWidth,
          object.y,
          (i - runStart) * moduleWidth,
          barsHeight,
        ),
        paint,
      );
      runStart = -1;
    }
  }
  if (hriHeight > 0 && ctx.font !== null) {
    const font = ctx.font;
    const hriSize = hriHeight * 0.85;
    const fitted = fitText(font, data, size.width, hriSize, 'shrink');
    font.setSize(fitted.size);
    const w = font.getTextWidth(fitted.text);
    canvas.drawText(
      fitted.text,
      object.x + (size.width - w) / 2,
      object.y + barsHeight + hriHeight * 0.9,
      paint,
      font,
    );
  }
  canvas.restore();
};

const drawQrObject = (canvas: SkCanvas, object: FloorObject): void => {
  const binding = getBinding(object);
  if (binding === null || binding.kind !== 'qr') return;
  const data = interpolateTokens(binding.data, renderState.ctx);

  canvas.save();
  applyRotation(canvas, object);
  if (data.length === 0) {
    drawInvalidCode(canvas, object);
    canvas.restore();
    return;
  }
  let matrix: boolean[][];
  try {
    matrix = encodeQrMatrix(data, binding.ecc);
  } catch {
    drawInvalidCode(canvas, object);
    canvas.restore();
    return;
  }
  const size = effectiveSize(object);
  const n = matrix.length + QR_QUIET_MODULES * 2;
  let moduleSize = Math.min(size.width, size.height) / n;
  if (renderState.forPrint) {
    moduleSize = Math.max(1, Math.floor(moduleSize));
  }
  const codeSize = moduleSize * n;
  const x0 =
    object.x + (size.width - codeSize) / 2 + QR_QUIET_MODULES * moduleSize;
  const y0 =
    object.y + (size.height - codeSize) / 2 + QR_QUIET_MODULES * moduleSize;
  const paint = inkPaint();
  for (let my = 0; my < matrix.length; my++) {
    const row = matrix[my];
    if (row === undefined) continue;
    for (let mx = 0; mx < row.length; mx++) {
      if (row[mx] !== true) continue;
      canvas.drawRect(
        Skia.XYWHRect(
          x0 + mx * moduleSize,
          y0 + my * moduleSize,
          moduleSize,
          moduleSize,
        ),
        paint,
      );
    }
  }
  canvas.restore();
};

const registeredState = { done: false };

/** Idempotent — called by the editor mount AND the offscreen renderer. */
export const registerLabelRenderers = (): void => {
  if (registeredState.done) return;
  registeredState.done = true;
  registerRenderer(LABEL_OBJECT_TYPE.TEXT, {
    draw: drawLabelText,
    hitShape: 'rect',
  });
  registerRenderer(LABEL_OBJECT_TYPE.BARCODE, {
    draw: drawBarcodeObject,
    hitShape: 'rect',
  });
  registerRenderer(LABEL_OBJECT_TYPE.QR, {
    draw: (canvas, object) => {
      drawQrObject(canvas, object);
    },
    hitShape: 'rect',
  });
};
